export interface EmailTemplate {
  id: string;
  name: string;
  subject: string;  // mapped from subject_en in DB
  content: string;  // mapped from body_en in DB
  description?: string;
  slug?: string;
  category?: string;
  organization_uuid: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface CreateEmailTemplateData {
  name: string;
  subject: string;
  content: string;
  description?: string;
}

export interface UpdateEmailTemplateData {
  name?: string;
  subject?: string;
  content?: string;
  description?: string;
}

// Available template variables organized by entity
interface TemplateVariable {
  key: string;
  label: string;
  description: string;
  example: string;
}

export interface TemplateVariableCategory {
  name: string;
  variables: TemplateVariable[];
}

export const TEMPLATE_VARIABLES: TemplateVariableCategory[] = [
  {
    name: "Candidate",
    variables: [
      { key: "candidate.name", label: "Name", description: "Full name of the candidate", example: "John Doe" },
      { key: "candidate.email", label: "Email", description: "Email address", example: "john.doe@example.com" },
      { key: "candidate.phone", label: "Phone", description: "Phone number", example: "+1 234 567 8900" },
      { key: "candidate.function", label: "Function", description: "Job function/role", example: "Software Engineer" },
      { key: "candidate.location", label: "Location", description: "Geographic location", example: "New York, NY" },
      { key: "candidate.linkedin", label: "LinkedIn", description: "LinkedIn profile URL", example: "https://linkedin.com/in/johndoe" },
      { key: "candidate.status", label: "Status", description: "Current status", example: "Active" },
      { key: "candidate.manager", label: "Manager", description: "Assigned manager", example: "Jane Smith" },
      { key: "candidate.created_at", label: "Created Date", description: "Date candidate was added", example: "2024-01-15" },
    ]
  },
  {
    name: "Job",
    variables: [
      { key: "job.title", label: "Title", description: "Job title", example: "Senior Software Engineer" },
      { key: "job.description", label: "Description", description: "Job description", example: "We are looking for..." },
      { key: "job.employment_type", label: "Employment Type", description: "Type of employment", example: "Full-time" },
      { key: "job.location_type", label: "Location Type", description: "Remote/On-site/Hybrid", example: "Remote" },
      { key: "job.seniority_level", label: "Seniority Level", description: "Experience level required", example: "Senior" },
      { key: "job.budget", label: "Budget", description: "Salary budget", example: "$80,000 - $120,000" },
      { key: "job.start_date", label: "Start Date", description: "Expected start date", example: "2024-03-01" },
      { key: "job.application_deadline", label: "Application Deadline", description: "Deadline for applications", example: "2024-02-15" },
      { key: "job.assigned_recruiter", label: "Assigned Recruiter", description: "Recruiter handling the position", example: "Sarah Johnson" },
      { key: "job.status", label: "Status", description: "Current job status", example: "open" },
    ]
  },
  {
    name: "Company",
    variables: [
      { key: "company.name", label: "Name", description: "Company name", example: "Tech Solutions Inc." },
      { key: "company.address", label: "Address", description: "Full address", example: "123 Main St, City, State 12345" },
      { key: "company.street", label: "Street", description: "Street address", example: "123 Main St" },
      { key: "company.city", label: "City", description: "City", example: "San Francisco" },
      { key: "company.country", label: "Country", description: "Country", example: "United States" },
      { key: "company.website_url", label: "Website", description: "Company website", example: "https://techsolutions.com" },
      { key: "company.assignee", label: "Assignee", description: "Assigned account manager", example: "Mike Wilson" },
      { key: "company.vat_number", label: "VAT Number", description: "VAT/Tax number", example: "US123456789" },
    ]
  },
  {
    name: "Contact",
    variables: [
      { key: "contact.name", label: "Name", description: "Contact full name", example: "Alice Brown" },
      { key: "contact.first_name", label: "First Name", description: "First name", example: "Alice" },
      { key: "contact.last_name", label: "Last Name", description: "Last name", example: "Brown" },
      { key: "contact.email", label: "Email", description: "Contact email", example: "alice.brown@company.com" },
      { key: "contact.phone", label: "Phone", description: "Contact phone", example: "+1 555 123 4567" },
      { key: "contact.function", label: "Function", description: "Job function at company", example: "HR Manager" },
    ]
  },
  {
    name: "Match",
    variables: [
      { key: "match.score", label: "Score", description: "Match score percentage", example: "85%" },
      { key: "match.description", label: "Description", description: "Match description", example: "Strong technical fit with relevant experience" },
      { key: "match.status", label: "Status", description: "Match status", example: "Under Review" },
      { key: "match.created_at", label: "Created Date", description: "Date match was created", example: "2024-01-20" },
    ]
  },
  {
    name: "User",
    variables: [
      { key: "user.first_name", label: "First Name", description: "Current user's first name", example: "John" },
      { key: "user.last_name", label: "Last Name", description: "Current user's last name", example: "Smith" },
      { key: "user.email", label: "Email", description: "Current user's email", example: "john.smith@company.com" },
    ]
  },
  {
    name: "System",
    variables: [
      { key: "system.current_date", label: "Current Date", description: "Today's date", example: "January 20, 2024" },
      { key: "system.current_time", label: "Current Time", description: "Current time", example: "2:30 PM" },
      { key: "system.organization_name", label: "Organization Name", description: "Your organization name", example: "Acme Recruiting" },
    ]
  }
];
